// TanStack Query hooks over @scribe/core's ScribeApiClient. Each hook falls back to the
// in-memory mock fixtures when isMock() is true, so screens never branch on MOCK themselves.
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './ApiContext';
import { isMock, mock } from './mock';
import { mockFeatures } from './mockFeatures';
import type {
  AdminBugReportList,
  ListAdminBugReportsParams,
  AdminFeature,
  AdminFeatureList,
  FeatureMap,
  UpdateFeatureSwitchRequest,
  AdminUsage,
  AiArtifact,
  AssistantAnswer,
  AssistantAskRequest,
  AudioPlaybackUrl,
  EngineChoice,
  Me,
  MergeSpeakersRequest,
  OrgSettings,
  Recording,
  RecordingList,
  Segment,
  Speaker,
  Tag,
  Transcript,
  TranscriptExportFormat,
  TranscriptVersion,
  TranscriptWords,
  FolderList,
  TagList,
  ProvisionUserRequest,
  UpdateOrgSettingsRequest,
  UserList,
  UserSummary,
  Vocabulary,
  VocabularyList,
  SearchResultList,
  VocabTerm,
  RecordingStatus,
  UpdateSpeakerRequest,
  PermissionLevel,
  PrincipalType,
  ResourceType,
  Share,
} from '@scribe/core/types';
import { isDeferred } from '@scribe/core/types';

// The root tsconfig enables exactOptionalPropertyTypes, and @scribe/core's request param
// types declare optional fields without `| undefined` — so an object literal that
// explicitly sets a key to `undefined` (e.g. `{ folder_id: filters.folderId }` where
// folderId may be undefined) is a type error. `compact` drops undefined-valued keys so the
// resulting object satisfies "key omitted" rather than "key present with value undefined".
function compact<T extends Record<string, unknown>>(
  obj: T,
): { [K in keyof T]?: NonNullable<T[K]> } {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as { [K in keyof T]?: NonNullable<T[K]> };
}

export interface ListRecordingsFilters {
  folderId?: string | undefined;
  tags?: string[] | undefined;
  status?: RecordingStatus | undefined;
  page?: number | undefined;
  /** SHARE-4: ownership scope — `'owned' | 'shared' | 'all'` (default `'all'`, matching the
   * pre-SHARE-4 unscoped behavior). Threaded into both the queryKey and the request. */
  scope?: 'owned' | 'shared' | 'all' | undefined;
}

// Recordings in a non-terminal status are actively moving through the transcription
// pipeline server-side (see openapi/scribe.yaml RecordingStatus) — poll them so both the
// list and detail screens transition to `completed` on their own once the backend
// finishes, without requiring a manual refresh.
const IN_PROGRESS_STATUSES = new Set<RecordingStatus>(['uploaded', 'queued', 'transcribing']);
const POLL_INTERVAL_MS = 5_000;

/** Whether `recording` is actively moving through the pipeline (worth polling). ONDEMAND-1: a
 * DEFERRED recording is `uploaded` forever — until someone opens, searches or asks about it —
 * so it is NOT in progress; without this carve-out a page of deferred rows would poll every
 * 5s indefinitely. `isDeferred` needs `transcription_deferred_at`, which is always null when
 * the feature is off, so this is unchanged behavior then. */
export function isRecordingInProgress(recording: Recording): boolean {
  return IN_PROGRESS_STATUSES.has(recording.status) && !isDeferred(recording);
}

/** `refetchInterval` for a page of recordings (and, with one item, a single recording). */
export function recordingsPollInterval(items: Recording[] | undefined): number | false {
  return items?.some(isRecordingInProgress) ? POLL_INTERVAL_MS : false;
}

export function useRecordings(filters: ListRecordingsFilters) {
  const { client } = useApi();
  return useQuery<RecordingList>({
    queryKey: ['recordings', filters],
    queryFn: async () => {
      if (isMock()) return mock.listRecordings(filters.scope, filters.folderId, filters.tags?.[0]);
      return client.listRecordings(
        compact({
          folder_id: filters.folderId,
          tag: filters.tags,
          status: filters.status,
          scope: filters.scope,
          page: filters.page,
        }),
      );
    },
    // Keep showing the previous page's rows while the next page loads, rather than
    // flashing the full loading state on every pagination click.
    placeholderData: (previous) => previous,
    // Auto-refresh the whole page while any row on it is still mid-pipeline, so an
    // upload's status visibly progresses (uploaded -> queued -> transcribing ->
    // completed/failed) without the user needing to open the recording or reload.
    refetchInterval: (query) => recordingsPollInterval(query.state.data?.items),
  });
}

export function useRecording(id: string | undefined) {
  const { client } = useApi();
  return useQuery<Recording | undefined>({
    queryKey: ['recording', id],
    queryFn: async () => {
      if (!id) return undefined;
      if (isMock()) return mock.getRecording(id);
      return client.getRecording(id);
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const recording = query.state.data;
      return recording ? recordingsPollInterval([recording]) : false;
    },
  });
}

// Presigned audio playback URLs are short-lived (see openapi/scribe.yaml
// AudioPlaybackUrl) — cache briefly and refetch on remount/staleness rather than caching
// indefinitely, but don't hammer the endpoint on every render either.
const AUDIO_URL_STALE_MS = 4 * 60 * 1000;

export function useAudioUrl(recordingId: string | undefined) {
  const { client } = useApi();
  return useQuery<AudioPlaybackUrl | undefined>({
    queryKey: ['audio-url', recordingId],
    queryFn: async () => {
      if (!recordingId) return undefined;
      if (isMock()) return mock.getRecordingAudioUrl(recordingId);
      return client.getRecordingAudioUrl(recordingId);
    },
    enabled: !!recordingId,
    staleTime: AUDIO_URL_STALE_MS,
  });
}

/** Lazily fetches a presigned playback URL for the recordings-list card's inline Play
 * preview (WEBUI-1 #1). Deliberately separate from `useAudioUrl` above: that hook is
 * always-enabled for the detail screen's persistent player, whereas a list card must NOT
 * request a presigned URL until the user actually clicks Play — `enabled` is wired to the
 * card's local `playing` state for exactly that. Uses its own `['recording-audio', id]`
 * query key (distinct from `useAudioUrl`'s `['audio-url', id]`) so the two never collide. */
export function useRecordingAudioUrl(id: string, enabled: boolean) {
  const { client } = useApi();
  return useQuery<AudioPlaybackUrl>({
    queryKey: ['recording-audio', id],
    queryFn: () => (isMock() ? mock.getRecordingAudioUrl(id) : client.getRecordingAudioUrl(id)),
    enabled,
    staleTime: 60_000, // presigned URL is short-lived but fine to reuse briefly; no polling
  });
}

/** Lists all transcript versions for a recording (metadata only, newest first). TVER-3:
 * drives the version switcher on RecordingDetail — hidden whenever only one version exists. */
export function useTranscriptVersions(recordingId: string | undefined) {
  const { client } = useApi();
  return useQuery<TranscriptVersion[]>({
    queryKey: ['transcript-versions', recordingId],
    queryFn: () =>
      isMock() ? mock.listTranscriptVersions() : client.listRecordingTranscripts(recordingId!),
    enabled: !!recordingId,
  });
}

export function useTranscript(recordingId: string | undefined, transcriptId?: string) {
  const { client } = useApi();
  return useQuery<Transcript | undefined>({
    queryKey: ['transcript', recordingId, transcriptId ?? 'authoritative'],
    queryFn: async () => {
      if (!recordingId) return undefined;
      if (isMock()) return mock.getTranscript();
      return client.getRecordingTranscript(recordingId, compact({ transcript_id: transcriptId }));
    },
    enabled: !!recordingId,
  });
}

/** Word timings for the viewed transcript version (REPLAY-2). Fetches lazily: `enabled`
 * is wired to the resolved version's `has_raw_json` so words-less versions (pure-live,
 * derived, imported) never issue the request. A `words_available: false` body is a normal
 * cached outcome — retry: false + staleTime: Infinity mean no refetch/retry loops. */
export function useTranscriptWords(
  recordingId: string | undefined,
  transcriptId: string | undefined,
  enabled: boolean,
) {
  const { client } = useApi();
  return useQuery<TranscriptWords>({
    queryKey: ['transcript-words', recordingId, transcriptId ?? 'authoritative'],
    queryFn: () =>
      isMock()
        ? mock.getTranscriptWords()
        : client.getTranscriptWords(recordingId!, compact({ transcript_id: transcriptId })),
    enabled: !!recordingId && enabled,
    staleTime: Infinity,
    retry: false,
  });
}

export function useTriggerTranscription() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      recordingId,
      ...jobSpec
    }: {
      recordingId: string;
      language?: string;
      max_speakers?: number;
      vocabulary_name?: string;
      pii_redaction?: boolean;
      /** TRIM-1: false = transcribe without silence trimming (per-recording opt-out). */
      trim_silence?: boolean | null;
    }) => {
      if (isMock()) return mock.getRecording(recordingId)!;
      return client.triggerTranscription(recordingId, jobSpec);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['recording', vars.recordingId] });
      qc.invalidateQueries({ queryKey: ['recordings'] });
      // Re-transcription mints a new authoritative version — refresh the version list and
      // the authoritative transcript's cache entry.
      qc.invalidateQueries({ queryKey: ['transcript-versions', vars.recordingId] });
      qc.invalidateQueries({ queryKey: ['transcript', vars.recordingId, 'authoritative'] });
    },
  });
}

/**
 * Bulk-edits segment text (PATCH /recordings/{id}/transcript/segments). Applies an
 * optimistic update to the cached `['transcript', recordingId]` query so the edited text
 * shows immediately; rolls back to the prior transcript snapshot on error.
 */
export function useUpdateTranscriptSegments(recordingId: string, transcriptId?: string) {
  const { client } = useApi();
  const qc = useQueryClient();
  const queryKey = ['transcript', recordingId, transcriptId ?? 'authoritative'];

  return useMutation({
    mutationFn: async (edits: { segment_id: string; text: string }[]) => {
      if (isMock()) {
        const current = qc.getQueryData<Transcript>(queryKey);
        const byId = new Map(edits.map((e) => [e.segment_id, e.text]));
        return {
          segments: (current?.segments ?? []).map((s) =>
            byId.has(s.id) ? { ...s, text: byId.get(s.id)!, edited_at: new Date().toISOString() } : s,
          ),
        };
      }
      return client.updateTranscriptSegments(recordingId, { edits }, compact({ transcript_id: transcriptId }));
    },
    onMutate: async (edits) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<Transcript>(queryKey);
      if (previous) {
        const byId = new Map(edits.map((e) => [e.segment_id, e.text]));
        qc.setQueryData<Transcript>(queryKey, {
          ...previous,
          segments: previous.segments.map((s) =>
            byId.has(s.id)
              ? { ...s, text: byId.get(s.id)!, edited_at: new Date().toISOString() }
              : s,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _edits, context) => {
      if (context?.previous) qc.setQueryData(queryKey, context.previous);
    },
    onSuccess: (data) => {
      const current = qc.getQueryData<Transcript>(queryKey);
      if (!current) return;
      const byId = new Map(data.segments.map((s: Segment) => [s.id, s]));
      qc.setQueryData<Transcript>(queryKey, {
        ...current,
        segments: current.segments.map((s) => byId.get(s.id) ?? s),
      });
    },
  });
}

/** Renames/assigns a diarized speaker (PUT /recordings/{id}/speakers/{label}). Updates the
 * cached transcript's speaker list in place so the transcript view reflects the new display
 * name immediately without a full refetch. */
export function useUpdateSpeaker(recordingId: string, transcriptId?: string) {
  const { client } = useApi();
  const qc = useQueryClient();
  // Speakers are per-recording rows shared by all versions, but the cached entry to patch
  // stays keyed to the viewed (active) version only — switching versions refetches and
  // heals any staleness (see docs/specs/tver-3.md).
  const queryKey = ['transcript', recordingId, transcriptId ?? 'authoritative'];

  return useMutation({
    mutationFn: async ({ label, body }: { label: string; body: UpdateSpeakerRequest }) => {
      if (isMock()) {
        const current = qc.getQueryData<Transcript>(queryKey);
        const existing = current?.speakers?.find((s) => s.label === label);
        const speaker: Speaker = {
          id: existing?.id ?? `spk-${label}`,
          recording_id: recordingId,
          label,
          display_name: body.display_name ?? existing?.display_name ?? null,
          resolved_user_id: body.resolved_user_id ?? existing?.resolved_user_id ?? null,
        };
        return speaker;
      }
      return client.updateRecordingSpeaker(recordingId, label, body);
    },
    onSuccess: (speaker) => {
      const current = qc.getQueryData<Transcript>(queryKey);
      if (!current) return;
      const speakers = current.speakers ?? [];
      const idx = speakers.findIndex((s) => s.label === speaker.label);
      const nextSpeakers =
        idx >= 0
          ? speakers.map((s, i) => (i === idx ? speaker : s))
          : [...speakers, speaker];
      qc.setQueryData<Transcript>(queryKey, { ...current, speakers: nextSpeakers });
    },
  });
}

/** Merges diarized speakers (POST /recordings/{id}/speakers/merge), reassigning every
 * affected segment's speaker_label and dropping the now-unused Speaker rows. Refetches the
 * transcript afterward since segment-level speaker_label reassignment is easier to get
 * correct from a server-authoritative refetch than to replicate client-side. */
export function useMergeSpeakers(recordingId: string, transcriptId?: string) {
  const { client } = useApi();
  const qc = useQueryClient();
  const queryKey = ['transcript', recordingId, transcriptId ?? 'authoritative'];

  return useMutation({
    mutationFn: async (body: MergeSpeakersRequest) => {
      if (isMock()) {
        const current = qc.getQueryData<Transcript>(queryKey);
        const fromSet = new Set(body.from);
        const reassigned = current?.segments.filter((s) => fromSet.has(s.speaker_label)).length ?? 0;
        const speaker: Speaker = {
          id: `spk-${body.into}`,
          recording_id: recordingId,
          label: body.into,
          display_name: null,
          resolved_user_id: null,
        };
        return { speaker, segments_reassigned: reassigned };
      }
      return client.mergeRecordingSpeakers(recordingId, body);
    },
    onSuccess: (result, vars) => {
      const current = qc.getQueryData<Transcript>(queryKey);
      if (!current) return;
      const fromSet = new Set(vars.from);
      const speakers = (current.speakers ?? []).filter((s) => !fromSet.has(s.label));
      const idx = speakers.findIndex((s) => s.label === result.speaker.label);
      const nextSpeakers =
        idx >= 0
          ? speakers.map((s, i) => (i === idx ? result.speaker : s))
          : [...speakers, result.speaker];
      qc.setQueryData<Transcript>(queryKey, {
        ...current,
        speakers: nextSpeakers,
        segments: current.segments.map((s) =>
          fromSet.has(s.speaker_label) ? { ...s, speaker_label: vars.into } : s,
        ),
      });
      // Segment reassignment is authoritative server-side; refetch to be safe against any
      // drift between this optimistic merge and what the backend actually persisted.
      void qc.invalidateQueries({ queryKey });
    },
  });
}

/** Downloads the recording's transcript export (GET /recordings/{id}/transcript/export) as
 * a Blob the caller can hand to an <a download> link. Not a TanStack Query mutation cache
 * concern beyond isolating the isMock() branch — callers trigger this directly on click. */
export function useExportTranscript(recordingId: string, transcriptId?: string) {
  const { client } = useApi();
  return useMutation({
    mutationFn: async (format: TranscriptExportFormat) => {
      if (isMock()) {
        return { text: mock.exportTranscript(format), format };
      }
      const text = await client.exportTranscript(recordingId, compact({ format, transcript_id: transcriptId }));
      return { text, format };
    },
  });
}

export function useDeleteRecording() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (isMock()) return;
      return client.deleteRecording(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recordings'] }),
  });
}

export function useFolders(parentId?: string | undefined) {
  const { client } = useApi();
  return useQuery<FolderList>({
    queryKey: ['folders', parentId ?? null],
    queryFn: async () => {
      if (isMock()) return mock.listFolders();
      return client.listFolders(compact({ parent_id: parentId }));
    },
  });
}

export function useCreateFolder() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; parentId?: string | null | undefined }) => {
      if (isMock()) return mock.createFolder({ name: input.name, parent_id: input.parentId ?? null });
      return client.createFolder({ name: input.name, parent_id: input.parentId ?? null });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders'] }),
  });
}

/** Renames a folder (PUT /folders/{id}). */
export function useUpdateFolder() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; name: string }) => {
      if (isMock()) return mock.updateFolder(input.id, { name: input.name });
      return client.updateFolder(input.id, { name: input.name });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders'] }),
  });
}

export function useDeleteFolder() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (isMock()) return mock.deleteFolder(id);
      return client.deleteFolder(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders'] }),
  });
}

export function useTags() {
  const { client } = useApi();
  return useQuery<TagList>({
    queryKey: ['tags'],
    queryFn: async () => {
      if (isMock()) return mock.listTags();
      return client.listTags();
    },
  });
}

export function useCreateTag() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; color?: string | null | undefined }) => {
      if (isMock()) return mock.createTag({ name: input.name, color: input.color ?? null });
      return client.createTag({ name: input.name, color: input.color ?? null });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tags'] }),
  });
}

/** Renames/recolors a tag (PUT /tags/{id}). */
export function useUpdateTag() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; name?: string; color?: string | null }) => {
      const body = compact({ name: input.name, color: input.color });
      if (isMock()) return mock.updateTag(input.id, body);
      return client.updateTag(input.id, body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tags'] }),
  });
}

export function useDeleteTag() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (isMock()) return mock.deleteTag(id);
      return client.deleteTag(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tags'] }),
  });
}

/** Moves a recording to a folder (or removes it from one, with `folderId: null`). */
export function useMoveRecordingToFolder(recordingId: string) {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (folderId: string | null) => {
      if (isMock()) return mock.moveRecordingToFolder(recordingId, folderId);
      return client.moveRecording(recordingId, folderId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recording', recordingId] });
      qc.invalidateQueries({ queryKey: ['recordings'] });
    },
  });
}

/** Tag ids currently attached to a recording. */
export function useRecordingTagIds(recordingId: string | undefined) {
  const { client } = useApi();
  return useQuery<string[]>({
    queryKey: ['recording-tags', recordingId],
    queryFn: async () => {
      if (!recordingId) return [];
      if (isMock()) return mock.getRecordingTagIds(recordingId);
      return (await client.getRecordingTags(recordingId)).map((t) => t.id);
    },
    enabled: !!recordingId,
  });
}

/** Full tag objects for a recording — powers the recordings-list card's tag chips (WEBUI-1
 * #4), mirroring the detail view's tag-editor approach (`client.getRecordingTags`).
 * Deliberately keyed `['recording-tag-objects', id]` rather than reusing
 * `useRecordingTagIds`'s `['recording-tags', id]` key: same recording, but that hook's cache
 * entry is a bare `string[]` of tag ids for the detail screen's checkbox editor — sharing one
 * key between a `Tag[]`-returning and a `string[]`-returning query would let one hook's
 * response satisfy the other's differently-shaped consumer straight out of cache (e.g.
 * navigating list -> detail for the same recording).
 * N-query note: one small request per visible card, cached with a long staleTime and NOT
 * polled (unlike the list query) — acceptable for current page sizes; a batch/embedded-tags
 * optimization is a later, out-of-scope follow-up. */
export function useRecordingTags(id: string) {
  const { client } = useApi();
  return useQuery<Tag[]>({
    queryKey: ['recording-tag-objects', id],
    queryFn: () => (isMock() ? mock.getRecordingTags(id) : client.getRecordingTags(id)),
    staleTime: 30_000, // no refetchInterval — tags change rarely; avoid N polling loops
  });
}

/** ~3 minutes of 5s polls while a summary is known to be in flight (then back to 15s). */
const SUMMARIZING_MAX_POLLS = 36;

/** AI-3: lists the `kind: 'summary'` AI artifacts for a recording. The summary lands
 * asynchronously shortly after the recording completes (AI-2 runs post-persist), so this
 * polls ONLY in the empty+completed window and stops as soon as data exists — matches
 * `useRecording`'s in-progress-status polling pattern above. */
export function useRecordingSummary(
  recordingId: string | undefined,
  recordingStatus?: RecordingStatus,
  /** ONDEMAND-1: a summary pass is known to be running (e.g. an import's first open) — poll
   * faster, but bounded (SUMMARIZING_MAX_POLLS), so a stuck pass can't poll forever at 5s. */
  summarizing = false,
) {
  const { client } = useApi();
  return useQuery<AiArtifact[]>({
    queryKey: ['recording-artifacts', recordingId, 'summary'],
    enabled: !!recordingId,
    queryFn: () =>
      isMock() ? mock.listRecordingArtifacts(recordingId!, 'summary') : client.listRecordingArtifacts(recordingId!, 'summary'),
    refetchInterval: (query) => {
      if (recordingStatus !== 'completed' || (query.state.data?.length ?? 0) > 0) return false;
      if (summarizing && query.state.dataUpdateCount < SUMMARIZING_MAX_POLLS) return POLL_INTERVAL_MS;
      return 15_000;
    },
    staleTime: 60_000,
  });
}

/** Replaces the full set of tag ids attached to a recording. */
export function useUpdateRecordingTags(recordingId: string) {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tagIds: string[]) => {
      if (isMock()) return mock.setRecordingTagIds(recordingId, tagIds);
      return (await client.setRecordingTags(recordingId, tagIds)).map((t) => t.id);
    },
    onSuccess: (tagIds) => qc.setQueryData(['recording-tags', recordingId], tagIds),
  });
}

export function useSearch(q: string, page = 1) {
  const { client } = useApi();
  return useQuery<SearchResultList>({
    queryKey: ['search', q, page],
    queryFn: async () => {
      if (isMock()) return mock.search(q);
      return client.searchTranscripts({ q, page });
    },
    enabled: q.trim().length > 0,
    placeholderData: (previous) => previous,
  });
}

/** Assistant Q&A (`POST /assistant/ask`). A mutation, not a query: every ask is a fresh,
 * metered model call, so nothing is cached, deduped, retried, or refetched on focus. */
export function useAssistantAsk() {
  const { client } = useApi();
  return useMutation<AssistantAnswer, Error, AssistantAskRequest>({
    mutationFn: async (body) => (isMock() ? mock.askAssistant(body) : client.askAssistant(body)),
    retry: false,
  });
}

export function useVocabularies() {
  const { client } = useApi();
  return useQuery<VocabularyList>({
    queryKey: ['vocabularies'],
    queryFn: async () => {
      if (isMock()) return mock.listVocabularies();
      return client.listVocabularies();
    },
  });
}

export function useCreateVocabulary() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; language: string; terms: VocabTerm[] }) => {
      if (isMock()) {
        const v: Vocabulary = {
          id: `vocab-${Date.now()}`,
          owner_id: 'mock-owner',
          name: input.name,
          language: input.language,
          published_version: null,
          terms: input.terms,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        return v;
      }
      return client.createVocabulary(input);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vocabularies'] }),
  });
}

export function useReplaceVocabularyTerms() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; terms: VocabTerm[] }) => {
      if (isMock()) return mock.listVocabularies().items[0]!;
      return client.replaceVocabularyTerms(input);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vocabularies'] }),
  });
}

export function usePublishVocabulary() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (isMock()) {
        return {
          id: `version-${Date.now()}`,
          vocabulary_id: id,
          version: 1,
          engine_state: 'PENDING' as const,
          published_by: 'mock-owner',
          published_at: new Date().toISOString(),
        };
      }
      return client.publishVocabulary(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vocabularies'] }),
  });
}

/** Delete a vocabulary — **admin only** (403 otherwise). Destructive for the whole tenant, since
 *  vocabularies are shared, and unlike an edit it is not undone by re-publishing. Also removes
 *  the AWS Transcribe vocabulary, so nothing engine-side is left untracked. */
export function useDeleteVocabulary() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (isMock()) return mock.deleteVocabulary(id);
      return client.deleteVocabulary(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vocabularies'] }),
  });
}

/** The caller's own profile — platform users.id, display name, email, is_admin.
 *  THE one `useMe` in the app: ADMIN-5's nav gate reads `.data?.is_admin` from this
 *  same hook — do not add a second. */
export function useMe() {
  const { client } = useApi();
  return useQuery<Me>({
    queryKey: ['me'],
    queryFn: () => (isMock() ? mock.getMe() : client.getMe()),
    staleTime: 5 * 60_000, // identity is session-stable; roles change only with a new token
  });
}

/** WHISPER-1: the engine choices this server accepts (`Me.transcription_engines`). Defaults to
 *  AWS only — while loading, on an older server, or with the feature off — so the engine
 *  selector stays hidden unless the server explicitly offers more. */
export function useEngineChoices(): readonly EngineChoice[] {
  const { data: me } = useMe();
  return me?.transcription_engines?.length ? me.transcription_engines : DEFAULT_ENGINES;
}
const DEFAULT_ENGINES: readonly EngineChoice[] = ['aws'];

/** Batch-resolves platform user ids to directory entries. One request per distinct id-set. */
export function useUsersByIds(ids: string[]) {
  const { client } = useApi();
  const unique = useMemo(() => Array.from(new Set(ids)).sort(), [ids]);
  const query = useQuery<UserList>({
    queryKey: ['users', 'by-id', unique],
    queryFn: () => (isMock() ? mock.listUsersByIds(unique) : client.listUsers({ ids: unique })),
    enabled: unique.length > 0,
    staleTime: 5 * 60_000,
  });
  const byId = useMemo(() => {
    const map = new Map<string, UserSummary>();
    for (const u of query.data?.items ?? []) map.set(u.id, u);
    return map;
  }, [query.data]);
  return { ...query, byId };
}

/** Directory typeahead (SHARE-3/SHARE-6 consume this; added here so the module is complete). */
export function useUserSearch(q: string) {
  const { client } = useApi();
  const trimmed = q.trim();
  return useQuery<UserList>({
    queryKey: ['users', 'search', trimmed],
    queryFn: () => (isMock() ? mock.searchUsers(trimmed) : client.listUsers({ q: trimmed })),
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  });
}

/** Lists shares on a resource (mock fixtures in mock mode, `GET /shares` otherwise). */
export function useShares(resourceType: ResourceType, resourceId: string) {
  const { client } = useApi();
  return useQuery<Share[]>({
    queryKey: ['shares', resourceType, resourceId],
    queryFn: async () => {
      if (isMock()) return mock.listShares(resourceType, resourceId);
      return client.listShares({ resource_type: resourceType, resource_id: resourceId });
    },
  });
}

export function useCreateShare(resourceType: ResourceType, resourceId: string) {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      principalType: PrincipalType;
      principalId: string;
      perm: PermissionLevel;
    }) => {
      if (isMock()) return mock.createShare(resourceType, resourceId, input);
      return client.createShare({
        resource_type: resourceType,
        resource_id: resourceId,
        principal_type: input.principalType,
        principal_id: input.principalId,
        perm: input.perm,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shares', resourceType, resourceId] }),
  });
}

/** SHARE-7: provision-then-share for a Graph-sourced (not-yet-signed-in) candidate — one
 *  chained mutation so ShareDialog's submit path stays "resolve to users.id, then the normal
 *  createShare" for both platform and external candidates. On a provision conflict (the
 *  supplied email already belongs to a different account) this rejects with the client's
 *  `ScribeApiError` (status 409) — ShareDialog maps that to its own copy; no share is created
 *  in that case. */
export function useProvisionAndShare(resourceType: ResourceType, resourceId: string) {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { person: ProvisionUserRequest; perm: PermissionLevel }) => {
      const provisioned = isMock()
        ? mock.provisionUser(input.person)
        : await client.provisionUser(input.person);
      if (isMock()) {
        return mock.createShare(resourceType, resourceId, {
          principalType: 'user',
          principalId: provisioned.id,
          perm: input.perm,
        });
      }
      return client.createShare({
        resource_type: resourceType,
        resource_id: resourceId,
        principal_type: 'user',
        principal_id: provisioned.id,
        perm: input.perm,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shares', resourceType, resourceId] }),
  });
}

export function useDeleteShare(resourceType: ResourceType, resourceId: string) {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { principalType: PrincipalType; principalId: string }) => {
      if (isMock()) return mock.deleteShare(resourceType, resourceId, input);
      return client.deleteShare({
        resource_type: resourceType,
        resource_id: resourceId,
        principal_type: input.principalType,
        principal_id: input.principalId,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shares', resourceType, resourceId] }),
  });
}

// ── ADMIN-5: org usage rollup + budget/settings (apps/web AdminScreen) ─────────────
//
// `enabled` on `useAdminUsage`/`useOrgSettings` defaults to `true` but is threaded through
// so AdminScreen can gate all three admin queries on `useMe().data?.is_admin === true`
// (docs/specs/admin-5.md §4 "States") — the screen can't conditionally call these hooks
// (they're unconditionally mounted so its forbidden-state branch still renders), so the
// gate has to be a query option, not caller-side branching.

/** Org + per-member usage/cost/budget-state for `month` (`YYYY-MM`). No polling — the
 * admin picks a month explicitly rather than watching a live-updating figure. */
export function useAdminUsage(month: string, enabled = true) {
  const { client } = useApi();
  return useQuery<AdminUsage>({
    queryKey: ['admin-usage', month],
    queryFn: () => (isMock() ? mock.getAdminUsage(month) : client.getAdminUsage(month)),
    enabled: enabled && !!month,
  });
}

export function useOrgSettings(enabled = true) {
  const { client } = useApi();
  return useQuery<OrgSettings>({
    queryKey: ['admin-settings'],
    queryFn: () => (isMock() ? mock.getOrgSettings() : client.getOrgSettings()),
    enabled,
  });
}

/** Budget states depend on org settings (default budget, warn threshold), so a settings
 * change invalidates the usage rollup too, not just the settings cache entry. */
export function useUpdateOrgSettings() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateOrgSettingsRequest) => {
      if (isMock()) return mock.updateOrgSettings(body);
      return client.updateOrgSettings(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-settings'] });
      qc.invalidateQueries({ queryKey: ['admin-usage'] });
    },
  });
}

export function useSetMemberBudget() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; monthlyBudgetUsd: number | null }) => {
      if (isMock()) return mock.setMemberBudget(input.userId, input.monthlyBudgetUsd);
      return client.setMemberBudget(input.userId, input.monthlyBudgetUsd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-usage'] }),
  });
}

// ── FEATURES-1: runtime feature switches (docs/specs/features-1.md) ────────────────

/** Admin: one page of every reporter's bug reports (Admin -> Features, under the bug reports
 * switch). Mock mode has none. */
export function useAdminBugReports(params: ListAdminBugReportsParams, enabled = true) {
  const { client } = useApi();
  return useQuery<AdminBugReportList>({
    queryKey: ['admin-bug-reports', params],
    queryFn: () =>
      isMock()
        ? Promise.resolve({
            items: [],
            page_info: { page: 1, page_size: params.page_size ?? 25, total: 0, has_more: false },
          })
        : client.listAdminBugReports(params),
    enabled,
    placeholderData: (previous) => previous,
  });
}

/** Admin: every switchable feature. Gated on `is_admin` like the other admin queries. */
export function useAdminFeatures(enabled = true) {
  const { client } = useApi();
  return useQuery<AdminFeatureList>({
    queryKey: ['admin-features'],
    queryFn: () => (isMock() ? mockFeatures.listAdminFeatures() : client.listAdminFeatures()),
    enabled,
  });
}

/** A switch change also changes what every user's UI shows, so it refreshes `['features']`. */
export function useUpdateFeatureSwitch() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation<AdminFeature, Error, { key: string } & UpdateFeatureSwitchRequest>({
    mutationFn: async ({ key, ...body }) => {
      if (isMock()) return mockFeatures.updateFeatureSwitch(key, body);
      return client.updateFeatureSwitch(key, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-features'] });
      qc.invalidateQueries({ queryKey: ['features'] });
    },
  });
}

/** Any signed-in user: `{key: enabled}` from `GET /features`. Cached for a minute, matching
 * the server's own per-process cache — a switch reaches open tabs on the next refetch. */
export function useFeatures() {
  const { client } = useApi();
  return useQuery<FeatureMap>({
    queryKey: ['features'],
    queryFn: () => (isMock() ? mockFeatures.listFeatures() : client.listFeatures()),
    staleTime: 60_000,
  });
}
